import { cn, Icon, Text } from '@mntn/ui-components'
import { useState } from 'react'
import { useAddBudget } from '@/hooks/api'
import { useOrderItem } from '@/hooks/api/use-order-item'
import { useTriggerAnimation } from '@/hooks/use-trigger-animation'
import { useSession } from '@/providers/session/session-provider'
import { OfferingCard } from '../shared'
import { AddBudgetPopover } from '../shared/add-budget-modal'
import { AllocationNotify } from '../shared/allocation-notify'
import { MfeThemeWrapper, type Theme } from '../shared/mfe-theme-wrapper'
import { SpendMeter } from '../shared/spend-meter'
import { formatProgramNames, getOfferingText, hasSpendError } from '../shared/utils'
import { OrderCardSkeleton } from './order-card-skeleton'

/**
 * @note three layout types are supported
 * - row: used on audience campaign step
 * - column: used as part of deliverability column on audience, and impressions and inventory campaign step
 * - compact: single column layout - used on review and launch campaign step
 */
export type LayoutType = 'compact' | 'row' | 'column'

export type OrderCardProps = {
  campaignGroupId: number
  className?: string
  layout?: LayoutType
  /**
   * @note used to show the targeting everyone audience badge
   */
  appliedAudience?: boolean
  cacheInvalidationKey?: number
  /**
   * @note order card can be themed to select or slate - defaults to select
   */
  theme?: Theme
  /**
   * @note inverse tokens applied across order card if set to true
   */
  isInverse?: boolean
}

export function OrderCard({
  campaignGroupId,
  className,
  cacheInvalidationKey,
  appliedAudience = false,
  layout = 'column',
  theme = 'select',
  isInverse = false,
}: OrderCardProps) {
  const [hasAnimated, setHasAnimated] = useState(false)
  const { isLoaded: isSessionLoaded } = useSession()
  const isCompact = layout === 'compact'
  const isRow = layout === 'row'
  const isColumn = layout === 'column'

  const { data: orderItem, isLoading } = useOrderItem({
    campaignGroupId,
    cacheInvalidationKey,
    enabled: isSessionLoaded && !!campaignGroupId,
  })

  const { mutateAsync: addBudget, isPending: isAddingBudget } = useAddBudget()

  useTriggerAnimation(!isLoading && !!orderItem && !hasAnimated, setHasAnimated, 100)

  if (isLoading)
    return (
      <MfeThemeWrapper theme={theme}>
        <OrderCardSkeleton layout={layout} compact={isCompact} isInverse={isInverse} />
      </MfeThemeWrapper>
    )

  if (!orderItem) return null

  const offeringText = getOfferingText(orderItem.programs.length)
  const t = (token: string) => (isInverse ? `${token}-inverse` : token)
  return (
    <MfeThemeWrapper theme={theme}>
      <div
        className={cn(
          `flex flex-col w-full min-w-[15.75rem] gap-2 ${t('bg-surface-bg')}`,
          isCompact && 'pt-1',
          isColumn && `p-4 mt-3 h-full border ${t('border-border')} rounded`,
          isRow && `flex-row items-center p-6 gap-6 border ${t('border-border')}`,
          className,
        )}
      >
        {isCompact && (
          <Text variant="base-xs" className={`uppercase leading-5 ${t('text-header')}`}>
            Deal Targeted
          </Text>
        )}
        {isColumn && (
          <OfferingCard
            size={isRow ? 'sm' : 'full'}
            imageProps={{
              src: orderItem.coverMediaUrl || '',
              alt: orderItem.offeringName,
            }}
            className={`shadow-lg border ${t('border-border')}`}
          />
        )}
        <ConditionalWrapper
          condition={isRow}
          wrapper={children => <div className="flex items-start justify-between gap-4 w-full">{children}</div>}
        >
          <div className={cn('flex flex-col', isRow && 'flex-1 min-w-0')}>
            <Text variant={isRow ? 'h4' : 'h5'} className={`truncate w-full ${t('text-header')} leading-[1.75rem]`}>
              {orderItem.offeringName}
            </Text>
            <div className={cn(isRow && 'min-w-0')}>
              {isRow && (
                <Text variant="base-sm" className={cn('min-w-0 whitespace-normal', t('text-secondary'))}>
                  <Text inline variant="base-sm" className="font-bold">
                    {offeringText}:
                  </Text>{' '}
                  {formatProgramNames(orderItem.programs.map(program => program.programName))}
                </Text>
              )}
              {!isRow && (
                <Text variant="xs-sb" className={t('text-primary')}>
                  {offeringText}
                </Text>
              )}
            </div>
          </div>
          {isRow && appliedAudience && (
            <div
              className={`rounded-full ${t('bg-sub-surface-bg')} border ${t('border-border')} flex shrink-0 py-1.5 pr-4 pl-2 gap-2 items-center`}
            >
              <Icon name="Check" className={`size-6 stroke-2 bg-primary-accent ${t('text-header')} p-1 rounded-full`} />
              <Text variant="sm-sb" className={`${t('text-primary')}`}>
                Targeting Everyone
              </Text>
            </div>
          )}
          {!isRow && (
            <div className="flex flex-col gap-3">
              <SpendMeter
                spendAmount={orderItem.budgetMetrics.delivered}
                totalBudget={orderItem.budgetMetrics.total}
                hasSpendError={hasSpendError(orderItem)}
                inverse={isInverse}
              />
              {!isCompact && (
                <AddBudgetPopover
                  orderItem={orderItem}
                  isSubmitting={isAddingBudget}
                  side="left"
                  buttonLabel="Add Budget"
                  onSubmit={async ({ budget, offeringVersionId }) => {
                    return addBudget({
                      orderItemId: orderItem.orderItemId,
                      body: {
                        advertiserId: orderItem.advertiserId,
                        budget,
                        offeringVersionId,
                        audienceId: orderItem.audienceId ?? undefined,
                        predictionId: orderItem.predictionId ?? undefined,
                        isTest: orderItem.isTest,
                      },
                    })
                  }}
                />
              )}
              <AllocationNotify
                effectiveBudget={orderItem.budgetMetrics.effective}
                ineffectiveBudget={orderItem.budgetMetrics.ineffective}
                unallocatedBudget={orderItem.budgetMetrics.unallocated}
                inverse={isInverse}
              />
            </div>
          )}
        </ConditionalWrapper>
      </div>
    </MfeThemeWrapper>
  )
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
const ConditionalWrapper: React.FC<{
  condition: boolean
  wrapper: (children: React.ReactNode) => React.ReactNode
  children: React.ReactNode
}> = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children)
